// GA4 conversion events (task 26): sign_up and purchase.
//
// The only code that talks to GA. Components and the auth context call the
// trackers here and never touch window.gtag or window.dataLayer, the same
// rule as window.Paddle and paddleLoader.ts.
//
// gtag is defined synchronously as a queue, exactly as Google's snippet does,
// so the trackers never wait for the script. A blocked or failed script
// leaves a harmless queue. Every tracker swallows its own errors: nothing
// above them catches (the checkout eventCallback in paddleLoader.ts calls
// its handler with no try), and analytics must never break signup or
// checkout.
//
// The tag loads from index.tsx, inside the not-framed, config-OK branch,
// never from public/index.html: there it would also run for a framer and
// before the env switch could turn it off. The CSP allows the Google hosts
// in template.yaml; `npm start` serves no CSP, so only a deployed site
// proves them (backend/tests/unit/test_template_csp.py).

import { getGaMeasurementId } from './amplifyConfig';
import { GA_SCRIPT_BASE, PROD_APP_HOSTNAME } from '../constants';
import type { CatalogEntry } from '../services/reportsApi';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export interface PurchaseItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  /** Unit price after discount, as GA4 defines it. */
  price: number;
  /** Per-unit discount, when there was one. */
  discount?: number;
  quantity: number;
}

export interface PurchaseParams {
  transaction_id: string;
  currency: string;
  /** Subtotal after discount, excluding tax (GA4: never include tax). */
  value: number;
  tax?: number;
  items: PurchaseItem[];
}

let initialized = false;
const sentTransactions = new Set<string>();

const round2 = (n: number): number => Math.round(n * 100) / 100;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const finiteOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const nonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

// Load GA4 once per page. hostname is a parameter so tests never stub
// window.location (the isFramed(win = window) pattern in frameGuard.ts).
export const initAnalytics = (hostname: string = window.location.hostname): void => {
  const id = getGaMeasurementId();
  if (!id || initialized) {
    return;
  }

  // Never replace an existing dataLayer (Google's own instruction).
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  window.gtag = function gtag() {
    // gtag.js reads Arguments objects from the dataLayer and silently
    // ignores arrays, so this must push `arguments` itself.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  // debug_mode is off only when the parameter is absent: Google documents
  // that setting it to false does not disable it.
  window.gtag('config', id, hostname === PROD_APP_HOSTNAME ? {} : { debug_mode: true });

  const src = `${GA_SCRIPT_BASE}?id=${id}`;
  if (!document.querySelector(`script[src="${src}"]`)) {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('error', () => {
      // No values: the id is not personal, but nothing here needs it.
      console.warn('Analytics script failed to load');
    });
    document.head.appendChild(script);
  }

  initialized = true;
};

// One sign_up per newly verified account. Called from confirmRegistration in
// AuthContext, the one point where a new verification is unambiguous.
export const trackSignUp = (): void => {
  try {
    if (!initialized) {
      return;
    }
    window.gtag?.('event', 'sign_up', { method: 'email' });
  } catch {
    // Analytics must never break signup.
  }
};

// Build GA4 purchase params from a Paddle.js checkout.completed payload, or
// null when it is not one we can trust. Reads nothing from data.customer or
// data.custom_data: the buyer's email and Cognito sub live there.
export const toPurchaseParams = (
  data: unknown,
  catalog: readonly CatalogEntry[]
): PurchaseParams | null => {
  if (!isRecord(data)) {
    return null;
  }
  const transactionId = data.transaction_id;
  const currency = data.currency_code;
  const totals = data.totals;
  // GA4: never send an empty transaction id.
  if (!nonEmptyString(transactionId) || !nonEmptyString(currency) || !isRecord(totals)) {
    return null;
  }
  if (typeof totals.subtotal !== 'number' || !Number.isFinite(totals.subtotal)) {
    return null;
  }

  const subtotal = totals.subtotal;
  const discount = finiteOr(totals.discount, 0);
  const rawItems = Array.isArray(data.items) ? data.items : [];

  const items: PurchaseItem[] = [];
  for (const raw of rawItems) {
    if (!isRecord(raw) || !nonEmptyString(raw.price_id)) {
      continue;
    }
    const quantity =
      typeof raw.quantity === 'number' && Number.isInteger(raw.quantity) && raw.quantity >= 1
        ? raw.quantity
        : 1;
    const itemTotals = isRecord(raw.totals) ? raw.totals : {};
    const itemSubtotal = finiteOr(itemTotals.subtotal, 0);
    const itemDiscount = finiteOr(itemTotals.discount, 0);
    const entry = catalog.find((e) => e.priceId === raw.price_id);
    const product = isRecord(raw.product) ? raw.product : {};
    const itemName =
      entry?.label ||
      (nonEmptyString(product.name) ? product.name : '') ||
      (nonEmptyString(raw.price_name) ? raw.price_name : '') ||
      raw.price_id;

    const item: PurchaseItem = {
      item_id: raw.price_id,
      item_name: itemName,
      price: round2(Math.max(0, itemSubtotal - itemDiscount) / quantity),
      quantity,
    };
    if (entry) {
      item.item_category = entry.kind;
    }
    if (itemDiscount > 0) {
      item.discount = round2(itemDiscount / quantity);
    }
    items.push(item);
  }

  const params: PurchaseParams = {
    transaction_id: transactionId,
    currency,
    value: round2(Math.max(0, subtotal - discount)),
    items,
  };
  if (typeof totals.tax === 'number' && Number.isFinite(totals.tax)) {
    params.tax = totals.tax;
  }
  return params;
};

// One purchase per transaction, from the Account page's checkout.completed.
// GA also dedupes on transaction_id server-side; this keeps the page from
// sending a repeat in the first place.
export const trackPurchase = (data: unknown, catalog: readonly CatalogEntry[]): void => {
  try {
    if (!initialized) {
      return;
    }
    const purchase = toPurchaseParams(data, catalog);
    if (!purchase || sentTransactions.has(purchase.transaction_id)) {
      return;
    }
    sentTransactions.add(purchase.transaction_id);
    window.gtag?.('event', 'purchase', purchase);
  } catch {
    // Analytics must never break checkout.
  }
};

// Test seam. The module-level state is the point in a browser and a
// liability across tests.
export const resetAnalyticsForTests = (): void => {
  initialized = false;
  sentTransactions.clear();
};
